import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, List, Phone, Mail, ChevronLeft, ChevronRight, Link2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TZ = "Europe/Berlin";
const fmt = (iso, o) => new Date(iso).toLocaleString("de-DE", { timeZone: TZ, ...o });
const tagKey = (iso) => fmt(iso, { year: "numeric", month: "2-digit", day: "2-digit" });
const montag = (d) => { const x = new Date(d); x.setHours(12, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };

const ERGEBNISSE = { erreicht: "Erreicht, beraten", nicht_erreicht: "Nicht erreicht", rueckruf: "Rückruf vereinbart", anmeldung_geplant: "Anmeldung geplant", kein_interesse: "Kein Interesse" };
const MAILARTEN = { bestaetigung: "Bestätigung", erinnerung: "Erinnerung (8 Uhr)", verschoben: "Verschiebung", absage: "Absage", intern: "Intern an Jean", tagesuebersicht: "Tagesübersicht an Jean" };
const StatusPill = ({ s }) => (
  <span className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full ${s === "gebucht" ? "bg-emerald-50 text-emerald-700" : s === "erledigt" ? "bg-sky-50 text-sky-700" : "bg-stone-100 text-stone-500 line-through"}`}>{s}</span>
);

const ErgebnisDialog = ({ termin, onClose, onDone }) => {
  const [erg, setErg] = useState(termin.ergebnis || "");
  const [notiz, setNotiz] = useState(termin.gespraechsnotiz || "");
  const save = async () => {
    try {
      const r = await axios.post(`${API}/termine/${termin.id}/ergebnis`, { ergebnis: erg, notiz });
      toast.success(r.data.rueckruf_am ? `Gespeichert – Rückruf-Aufgabe für Jean am ${r.data.rueckruf_am.split("-").reverse().join(".")} angelegt` : "Ergebnis gespeichert – auch beim Kontakt");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="termin-ergebnis-dialog">
        <DialogHeader><DialogTitle>Gespräch mit {termin.vorname} {termin.nachname}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-2">
          {Object.entries(ERGEBNISSE).map(([k, v]) => (
            <button key={k} onClick={() => setErg(k)} data-testid={`ergebnis-${k}`}
              className={`text-sm text-left px-3 py-2 rounded border transition-colors ${erg === k ? "bg-[#3D3835] text-white border-[#3D3835]" : "border-stone-300 hover:bg-stone-100"}`}>{v}</button>
          ))}
        </div>
        <textarea rows={4} value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="Notiz zum Gespräch (z. B. Kurswunsch, Förderung, nächster Schritt)"
          className="w-full border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="ergebnis-notiz" />
        <button onClick={save} disabled={!erg} data-testid="ergebnis-speichern"
          className="w-full rounded bg-[#3D3835] text-white py-2 text-sm hover:bg-black transition-colors disabled:opacity-50">Speichern</button>
      </DialogContent>
    </Dialog>
  );
};

const BeispielMail = ({ onClose }) => {
  const [art, setArt] = useState("bestaetigung");
  const [m, setM] = useState(null);
  useEffect(() => { setM(null); axios.get(`${API}/termine/beispiel-mail?art=${art}`).then((r) => setM(r.data)); }, [art]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="beispiel-mail-dialog">
        <DialogHeader><DialogTitle>Beispiel-Mails (Muster-Daten)</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">
          {Object.entries(MAILARTEN).map(([k, v]) => (
            <button key={k} onClick={() => setArt(k)} data-testid={`beispiel-${k}`}
              className={`text-xs px-2 py-1 rounded border ${art === k ? "bg-[#3D3835] text-white" : "border-stone-300"}`}>{v}</button>
          ))}
        </div>
        {m && (
          <div className="text-sm space-y-1">
            <div><b>An:</b> {m.an}</div>
            <div data-testid="beispiel-betreff"><b>Betreff:</b> {m.betreff}</div>
            <iframe title="Beispiel-Mail" srcDoc={m.html} sandbox="" className="w-full h-[55vh] border border-stone-200 rounded" data-testid="beispiel-mail-inhalt" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const TerminKarte = ({ t, onAktion }) => (
  <div className="border border-[#E0D6CA] rounded-lg p-3 bg-white" data-testid={`termin-${t.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="font-semibold text-[#3D3835]">{fmt(t.slot_start, { weekday: "short", day: "2-digit", month: "2-digit" })} · {fmt(t.slot_start, { hour: "2-digit", minute: "2-digit" })} Uhr</div>
        <div className="text-sm">{t.vorname} {t.nachname} <span className="text-[#8E7B71]">· {t.interesse}</span></div>
        <div className="text-xs text-[#8E7B71] flex flex-wrap gap-3 mt-1">
          <a href={`tel:${t.telefon}`} className="flex items-center gap-1 hover:underline"><Phone size={12} />{t.telefon}</a>
          <span className="flex items-center gap-1"><Mail size={12} />{t.email}</span>
          {t.kontakt_neu && <span className="text-[#9A3B2E]">neuer Kontakt</span>}
          {t.erinnerung_gesendet_am && <span>Erinnerung raus</span>}
        </div>
        {t.ergebnis_label && <div className="text-xs mt-1 text-sky-800" data-testid={`termin-ergebnis-${t.id}`}><b>{t.ergebnis_label}</b>{t.gespraechsnotiz ? ` – ${t.gespraechsnotiz}` : ""}</div>}
      </div>
      <StatusPill s={t.status} />
    </div>
    {t.status !== "storniert" && (
      <div className="flex flex-wrap gap-2 mt-2">
        <button onClick={() => onAktion("ergebnis", t)} className="text-xs px-3 py-1 rounded border border-sky-300 text-sky-800 hover:bg-sky-50 transition-colors" data-testid={`termin-ergebnis-btn-${t.id}`}>{t.ergebnis ? "Ergebnis ändern" : "Ergebnis / Notiz"}</button>
        {t.status === "gebucht" && <>
        <button onClick={() => onAktion("verschieben", t)} className="text-xs px-3 py-1 rounded border border-stone-300 hover:bg-stone-100 transition-colors" data-testid={`termin-verschieben-${t.id}`}>Verschieben</button>
        <button onClick={() => onAktion("stornieren", t)} className="text-xs px-3 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 transition-colors" data-testid={`termin-stornieren-${t.id}`}>Stornieren</button>
        </>}
      </div>
    )}
  </div>
);

const Woche = ({ termine, start, onAktion }) => {
  const tage = [...Array(5)].map((_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="termine-kalender">
      {tage.map((d) => {
        const key = d.toLocaleDateString("de-DE", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" });
        const liste = termine.filter((t) => tagKey(t.slot_start) === key);
        return (
          <div key={key} className="bg-[#F5F0EB] rounded-lg p-2 min-h-[80px]">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#8E7B71] mb-2">{d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit" })}</div>
            <div className="space-y-2">{liste.length ? liste.map((t) => <TerminKarte key={t.id} t={t} onAktion={onAktion} />) : <div className="text-xs text-stone-400">keine Termine</div>}</div>
          </div>
        );
      })}
    </div>
  );
};

const VerschiebenDialog = ({ termin, onClose, onDone }) => {
  const [tage, setTage] = useState([]);
  const [idx, setIdx] = useState(0);
  const [informieren, setInformieren] = useState(true);
  useEffect(() => { axios.get(`${API}/termine/freie-slots`).then((r) => setTage(r.data.tage)); }, []);
  const waehlen = async (start) => {
    try {
      const r = await axios.post(`${API}/termine/${termin.id}/verschieben`, { slot_start: start, kunde_informieren: informieren });
      toast.success(`Verschoben auf ${r.data.termin}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Verschieben fehlgeschlagen"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="termin-verschieben-dialog">
        <DialogHeader><DialogTitle>Termin verschieben: {termin.vorname} {termin.nachname}</DialogTitle></DialogHeader>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {tage.map((t, i) => (
            <button key={t.datum} onClick={() => setIdx(i)} data-testid={`verschieben-tag-${t.datum}`} className={`shrink-0 text-xs px-2 py-1 rounded border ${i === idx ? "bg-[#3D3835] text-white" : "border-stone-300"}`}>{t.label}</button>
          ))}
        </div>
        <div className="grid grid-cols-4 gap-2 max-h-60 overflow-y-auto">
          {(tage[idx]?.slots || []).map((s) => (
            <button key={s.start} onClick={() => waehlen(s.start)} className="text-sm py-2 rounded border border-stone-300 hover:bg-[#FBEBE2] transition-colors" data-testid={`verschieben-slot-${s.zeit.replace(":", "")}`}>{s.zeit}</button>
          ))}
        </div>
        <label className="text-sm flex items-center gap-2 mt-2">
          <input type="checkbox" checked={informieren} onChange={(e) => setInformieren(e.target.checked)} data-testid="verschieben-informieren" />
          Interessent per E-Mail über den neuen Termin informieren
        </label>
      </DialogContent>
    </Dialog>
  );
};

export default function Termine() {
  const [daten, setDaten] = useState({ termine: [], mails_aktiv: false });
  const [ansicht, setAnsicht] = useState("liste");
  const [nurAktiv, setNurAktiv] = useState(true);
  const [woche, setWoche] = useState(() => montag(new Date()));
  const [verschieben, setVerschieben] = useState(null);
  const [ergebnisFuer, setErgebnisFuer] = useState(null);
  const [beispiel, setBeispiel] = useState(false);
  const laden = () => axios.get(`${API}/termine`).then((r) => setDaten(r.data)).catch(() => toast.error("Termine konnten nicht geladen werden"));
  useEffect(() => { laden(); }, []);

  const aktion = async (art, t) => {
    if (art === "verschieben") return setVerschieben(t);
    if (art === "ergebnis") return setErgebnisFuer(t);
    const grund = window.prompt(`Termin von ${t.vorname} ${t.nachname} stornieren? Die Person bekommt eine Absage mit Link für einen neuen Termin. Optional Grund (nur intern):`, "");
    if (grund === null) return;
    try { await axios.post(`${API}/termine/${t.id}/stornieren`, { grund }); toast.success("Termin storniert"); laden(); }
    catch (e) { toast.error(e.response?.data?.detail || "Stornieren fehlgeschlagen"); }
  };

  const jetzt = Date.now();
  const sichtbar = useMemo(() => daten.termine.filter((t) => !nurAktiv || (t.status !== "storniert" && new Date(t.slot_start).getTime() >= new Date().setHours(0, 0, 0, 0)) || (t.status === "gebucht" && !t.ergebnis)), [daten, nurAktiv, jetzt]);
  const link = `${window.location.origin}/termin`;

  return (
    <div className="page-container" data-testid="termine-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="text-3xl font-serif text-[#3D3835]">Telefonberatungen</h1>
          <p className="text-sm text-[#8E7B71]">Gebuchte Termine aus der öffentlichen Buchungsseite</p>
        </div>
        <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Buchungslink kopiert"); }} className="text-sm flex items-center gap-2 px-3 py-2 rounded border border-stone-300 hover:bg-stone-100 transition-colors" data-testid="termine-link-kopieren">
          <Link2 size={14} /> Buchungslink kopieren
        </button>
      </div>
      {daten.mails_aktiv && daten.test_empfaenger && (
        <div className="mb-4 text-sm rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-sky-900" data-testid="termine-mails-testmodus">
          Testmodus: Alle Termin-Mails (auch die an Interessenten) gehen vorerst nur an {daten.test_empfaenger}.{" "}
          <button onClick={() => setBeispiel(true)} className="underline font-medium" data-testid="termine-beispiel-mail-test">Beispiel-Mails ansehen</button>
        </div>
      )}
      {!daten.mails_aktiv && (
        <div className="mb-4 text-sm rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900" data-testid="termine-mails-aus">
          E-Mail-Versand ist ausgeschaltet: Bestätigungen und interne Benachrichtigungen werden nur protokolliert, nicht verschickt.{" "}
          <button onClick={() => setBeispiel(true)} className="underline font-medium" data-testid="termine-beispiel-mail">Beispiel-Mails ansehen</button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => setAnsicht("liste")} className={`text-sm flex items-center gap-1 px-3 py-1.5 rounded ${ansicht === "liste" ? "bg-[#3D3835] text-white" : "border border-stone-300"}`} data-testid="termine-ansicht-liste"><List size={14} /> Liste</button>
        <button onClick={() => setAnsicht("kalender")} className={`text-sm flex items-center gap-1 px-3 py-1.5 rounded ${ansicht === "kalender" ? "bg-[#3D3835] text-white" : "border border-stone-300"}`} data-testid="termine-ansicht-kalender"><CalendarDays size={14} /> Woche</button>
        <label className="text-sm flex items-center gap-1 ml-2"><input type="checkbox" checked={nurAktiv} onChange={(e) => setNurAktiv(e.target.checked)} data-testid="termine-nur-aktiv" /> nur anstehende</label>
        {ansicht === "kalender" && (
          <div className="flex items-center gap-1 ml-auto">
            <button onClick={() => setWoche(new Date(woche.getTime() - 7 * 864e5))} className="p-1.5 rounded border border-stone-300" data-testid="termine-woche-zurueck"><ChevronLeft size={16} /></button>
            <span className="text-sm px-2">ab {woche.toLocaleDateString("de-DE")}</span>
            <button onClick={() => setWoche(new Date(woche.getTime() + 7 * 864e5))} className="p-1.5 rounded border border-stone-300" data-testid="termine-woche-weiter"><ChevronRight size={16} /></button>
          </div>
        )}
      </div>
      {ansicht === "liste" ? (
        <div className="space-y-2" data-testid="termine-liste">
          {sichtbar.length ? sichtbar.map((t) => <TerminKarte key={t.id} t={t} onAktion={aktion} />) : <p className="text-sm text-[#8E7B71]">Keine Termine.</p>}
        </div>
      ) : <Woche termine={sichtbar} start={woche} onAktion={aktion} />}
      {beispiel && <BeispielMail onClose={() => setBeispiel(false)} />}
      {ergebnisFuer && <ErgebnisDialog termin={ergebnisFuer} onClose={() => setErgebnisFuer(null)} onDone={() => { setErgebnisFuer(null); laden(); }} />}
      {verschieben && <VerschiebenDialog termin={verschieben} onClose={() => setVerschieben(null)} onDone={() => { setVerschieben(null); laden(); }} />}
    </div>
  );
}
